import dayjs from 'dayjs'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect } from 'react'

import {
  calendarDateAtom,
  selectedInstructorIdAtom,
  selectedDateStringAtom,
  selectedTimeSlotsAtom,
  availableSessionsByInstructorAtom,
  availableTimeSlotsAtom,
  selectedSessionsAtom,
  selectedSessionsLocationsAtom,
  nextAvailableDateAtom,
  prevAvailableDateAtom,
  availableDateSetAtom,
} from '../../contexts/AtomPrivateSessionSearch'

// 寻找指定月份的第一个可用日期
const findFirstAvailableDate = (month, sessions) => {
  const startOfMonth = month.startOf('month')
  const endOfMonth = month.endOf('month')

  const availableDates = sessions
    .map((session) => dayjs(session.startTime))
    .filter((date) => date.isAfter(dayjs().startOf('day')) && date.isAfter(startOfMonth) && date.isBefore(endOfMonth))
    .sort((a, b) => a - b)

  return availableDates[0] || null
}

/**
 * 用于处理日期和时间选择的自定义Hook
 * 管理可用日期、时间段的选择，以及日期导航功能
 * 状态现在存储在 Jotai atoms 中，便于跨组件共享
 *
 * @param {Object} instructor - 当前选择的教练对象
 * @param {Array} availableSessions - 可用的课程会话数据
 * @returns {Object} - 包含状态和处理函数的对象
 */
export function useTimeSelection() {
  // 从 atoms 读取状态
  const selectedInstructorId = useAtom(selectedInstructorIdAtom)
  const [calendarDate, setCalendarDate] = useAtom(calendarDateAtom)
  const [selectedDateString, setSelectedDateString] = useAtom(selectedDateStringAtom)
  const [selectedTimeSlots, setSelectedTimeSlots] = useAtom(selectedTimeSlotsAtom)

  // 派生状态（自动更新）
  const availableSessionsByInstructor = useAtomValue(availableSessionsByInstructorAtom)
  const availableTimeSlots = useAtomValue(availableTimeSlotsAtom)
  const selectedSessions = useAtomValue(selectedSessionsAtom)
  const selectedSessionsLocations = useAtomValue(selectedSessionsLocationsAtom)
  const nextAvailableDate = useAtomValue(nextAvailableDateAtom)
  const prevAvailableDate = useAtomValue(prevAvailableDateAtom)
  const availableDateSet = useAtomValue(availableDateSetAtom)

  // 处理日期变更
  const handleDateChange = useCallback(
    (newDate) => {
      setCalendarDate(newDate)
      setSelectedDateString(dayjs(newDate).format('YYYY-MM-DD'))
      setSelectedTimeSlots(new Set())
    },
    [setCalendarDate, setSelectedDateString, setSelectedTimeSlots],
  )

  // Auto Select date
  useEffect(() => {
    // check if is empty
    if (availableSessionsByInstructor.length > 0) {
      // check if there is sessions on the calendarDate and set to selectedDateString
      const sessionsOnDate = availableSessionsByInstructor.filter((session) => dayjs(session.startTime).isSame(calendarDate, 'day'))
      if (sessionsOnDate.length > 0) {
        return
      }

      // find the first one and convert to date and set to calendarDate and selectedDateString
      const firstDate = availableSessionsByInstructor[0].startTime
      setCalendarDate(dayjs(firstDate))
      setSelectedDateString(dayjs(firstDate).format('YYYY-MM-DD'))
    }
  }, [availableSessionsByInstructor, setCalendarDate, setSelectedDateString])

  // 处理时间槽选择
  const handleTimeSlotSelect = useCallback(
    (slot) => {
      const newSelectedTimeSlots = new Set(selectedTimeSlots)

      if (newSelectedTimeSlots.has(slot)) {
        newSelectedTimeSlots.delete(slot)
      } else {
        newSelectedTimeSlots.add(slot)
      }
      setSelectedTimeSlots(newSelectedTimeSlots)
    },
    [selectedTimeSlots, setSelectedTimeSlots],
  )

  // 日期禁用判断（使用 useCallback 记忆化函数引用）
  // 注意：useCallback 已经提供了函数记忆化，useMemo 用于值而非函数
  const shouldDisableDate = useCallback(
    (date) => {
      // 如果 date 已经是 dayjs 对象，直接格式化；否则转换
      const dateStr = dayjs.isDayjs(date) ? date.format('YYYY-MM-DD') : dayjs(date).format('YYYY-MM-DD')
      return !availableDateSet.has(dateStr)
    },
    [availableDateSet],
  )

  // 跳转到下一个可用日期
  const handleNextDate = useCallback(() => {
    if (nextAvailableDate) {
      handleDateChange(nextAvailableDate)
    }
  }, [handleDateChange, nextAvailableDate])

  // 跳转到上一个可用日期
  const handlePrevDate = useCallback(() => {
    if (prevAvailableDate) {
      handleDateChange(prevAvailableDate)
    }
  }, [handleDateChange, prevAvailableDate])

  // 处理月份变更
  const handleMonthChange = useCallback(
    (month) => {
      if (selectedInstructorId === '' || availableSessionsByInstructor.length === 0) {
        return null
      }

      // check if calendarDate is the same as month
      if (calendarDate.isSame(month, 'month')) {
        return
      }

      // findFirstAvailableDate
      const firstAvailableDate = findFirstAvailableDate(month, availableSessionsByInstructor)
      if (firstAvailableDate) {
        handleDateChange(firstAvailableDate)
        return
      }

      // no available date, set to first day of month
      setCalendarDate(month)
      setSelectedDateString(month.format('YYYY-MM-DD'))
      setSelectedTimeSlots(new Set())
    },
    [calendarDate, selectedInstructorId, availableSessionsByInstructor, handleDateChange, setCalendarDate, setSelectedDateString, setSelectedTimeSlots],
  )

  return {
    // 状态
    calendarDate,
    selectedDateString,
    selectedTimeSlots,
    availableTimeSlots,
    selectedSessions,
    selectedSessionsLocations,
    nextAvailableDate,
    prevAvailableDate,

    // 处理函数
    handleDateChange,
    handleTimeSlotSelect,
    handleMonthChange,
    handleNextDate,
    handlePrevDate,

    // 辅助函数
    shouldDisableDate,
  }
}
